import {put, call, all} from 'redux-saga/effects';
import Blackout from "../../entities/blackout";
import * as Actions from "../actions/blackoutActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type BlackoutAction = {
    blackoutId: string;
    blackout: Blackout;
    blackouts: Blackout[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneBlackout(action: BlackoutAction) {
	const session = tabSession.captureGeneration();
	try {
		const blackout: Blackout = yield call(api.findOne, 'blackout', action.blackoutId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_FETCH_ONE_SUCCESS, blackoutId: action.blackoutId, blackout, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BLACKOUT_FETCH_ONE_FAILURE, blackoutId: action.blackoutId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {BlackoutAction} action
 */
export function* saveBlackout(action: BlackoutAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ BLACKOUT_SAVE saga', action.blackout?.id);

		if (action.blackout === undefined || action.blackout === null) {
			console.error('saveBlackout received a null');
			throw new Error('saveBlackout received a null');
		}

		if (!(action.blackout instanceof Blackout)) {
			action.blackout = new Blackout(action.blackout);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.blackout.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.blackout);
			const addResult = yield call(idb.add, 'blackout', action.blackout);
			if (addResult.isErr()) {
				console.error(`⮂ saveBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertBlackoutSave(action.blackout, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.BLACKOUT_SAVE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.BLACKOUT_SAVE, blackout: action.blackout, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertBlackoutSave(action.blackout, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.BLACKOUT_SAVE_FAILURE, blackout: action.blackout, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {BlackoutAction} action
 * @param {Blackout[]} action.blackouts
 */
export function* saveManyBlackouts(action: BlackoutAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ BLACKOUT_SAVE_MANY saga', action.blackouts.map(blackout => blackout.id).join(','));

        if (!Array.isArray(action.blackouts)) {
            console.error('saveManyBlackouts expected an array, received', action.blackouts);
            throw new Error('saveManyBlackouts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveBlackout), under these queue keys
		const dispatchedAddedOns: number[] = action.blackouts.map(blackout => blackout?.addedOn);
		const revertAll = () => action.blackouts.map((blackout, i) =>
			put(Actions.revertBlackoutSave(blackout, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Blackout[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.blackouts.length; i++) {
				const blackout = action.blackouts[i];
				blackout.persisted = false;
				ensureOperationId(blackout);
				toSave.push(blackout);
			}

			const addResult = yield call(idb.add, 'blackout', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyBlackouts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.BLACKOUT_SAVE_MANY_FAILURE, blackouts: action.blackouts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.BLACKOUT_SAVE_MANY, blackouts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.BLACKOUT_SAVE_MANY_FAILURE, blackouts: action.blackouts, status: 500, err});

			console.error(`Error saving ${action.blackouts.length} blackouts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {BlackoutAction} action
 */
export function* removeBlackout(action: BlackoutAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.blackout === undefined || action.blackout === null || !(action.blackout instanceof Blackout)) {
			console.error('removeBlackout expected type Blackout, received', action.blackout);
			reportQueued(false);
			throw new Error('removeBlackout expected type Blackout');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.blackout.status = 'D';
		action.blackout.addedOn = newHRTimestamp();
		action.blackout.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.blackout);
		const addResult = yield call(idb.add, 'blackout', action.blackout);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.BLACKOUT_REMOVE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_REMOVE, blackout: action.blackout});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BLACKOUT_REMOVE_FAILURE, blackout: action.blackout, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Blackout.
	*
	* @param {BlackoutAction} action
	*/
export function* restoreBlackout(action: BlackoutAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreBlackout expected getCurrentUserId() to return a value');
        return;
    }

	if (action.blackout === undefined || action.blackout === null) {
        console.error('restoreBlackout expected a Blackout, received null');
        throw new Error('restoreBlackout expected a Blackout, received null');
    }
    else if (!(action.blackout instanceof Blackout)) action.blackout = new Blackout(action.blackout); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.blackout.status = 'U';
		action.blackout.addedOn = newHRTimestamp();
		action.blackout.addedBy = userId;
		action.blackout.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.blackout);
		const addResult = yield call(idb.add, 'blackout', action.blackout);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BLACKOUT_RESTORE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_RESTORE, blackout: action.blackout});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BLACKOUT_RESTORE_FAILURE, blackout: action.blackout, status: 500, err});

		console.error('Error restoring from database', err);
	}
}